import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Package,
  Bot,
  Workflow,
  Code,
  Globe,
  Server,
  Plug,
  Users,
} from 'lucide-react';

/**
 * Scenario Detection:
 * - EVALUATING_BEFORE_USE: Looking at a third-party tool before installing
 * - EVALUATING_AFTER_USE: Checking a tool you've already been using
 * - EVALUATING_INSTALLED: Auditing something already in your system
 * - CREATING_OWN: Built something yourself with AI help
 * - USING_OTHERS_CODE: Incorporating someone else's code into your project
 * - HYBRID: Mix of your code and others' code
 */
type Scenario =
  | 'evaluating_before_use'
  | 'evaluating_after_use'
  | 'evaluating_installed'
  | 'creating_own'
  | 'using_others_code'
  | 'hybrid';

type ToolCategory = 'mcp' | 'agent' | 'workflow' | 'code' | 'api' | 'plugin' | 'server';

// Icon lookup keyed by tool category — must live outside the component so the
// reference is stable across renders (avoids react-hooks/static-components).
const TOOL_ICON_MAP: Record<ToolCategory, typeof Code> = {
  mcp: Plug,
  agent: Bot,
  workflow: Workflow,
  code: Code,
  api: Globe,
  server: Server,
  plugin: Package,
};

interface GrandmaLetterProps {
  pluginName: string;
  toolDescription?: string;
  vulnerabilities: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  recommendation: string;
  creatorVerified: boolean;
  creatorName: string;
  hasSecrets?: boolean;
  hasInjection?: boolean;
  hasAI?: boolean;
  reportDate: string;
  // Context detection props
  scanType?: string;
  targetUrl?: string;
  isExternalTool?: boolean;
  // Additional metadata for better detection
  metadata?: {
    repoMetadata?: {
      owner?: string;
      repo?: string;
      description?: string;
      stars?: number;
      language?: string;
    };
    filesScanned?: number;
    linesScanned?: number;
  };
  toolInfo?: {
    description?: string;
    capabilities?: string[];
    features?: string[];
  };
  // Override scenario if known
  scenarioOverride?: Scenario;
}

const GrandmaLetter = ({
  pluginName,
  toolDescription,
  vulnerabilities,
  recommendation,
  creatorVerified,
  creatorName,
  hasSecrets,
  hasInjection,
  hasAI: _hasAI,
  reportDate,
  scanType = 'code',
  targetUrl = '',
  isExternalTool = true,
  metadata,
  toolInfo,
  scenarioOverride,
}: GrandmaLetterProps) => {
  const totalIssues =
    vulnerabilities.critical + vulnerabilities.high + vulnerabilities.medium + vulnerabilities.low;
  const hasCritical = vulnerabilities.critical > 0;
  const hasHigh = vulnerabilities.high > 0;

  // ============================================
  // SCENARIO DETECTION
  // ============================================
  const detectScenario = (): Scenario => {
    if (scenarioOverride) return scenarioOverride;

    // Check if it's from GitHub or external source
    const isFromGitHub = targetUrl?.includes('github.com');
    const isFromNpm = targetUrl?.includes('npmjs.com') || targetUrl?.includes('npm:');
    const isFromPypi = targetUrl?.includes('pypi.org');
    const isExternal = isFromGitHub || isFromNpm || isFromPypi || isExternalTool;
    const isLocalPath =
      targetUrl?.startsWith('./') || targetUrl?.startsWith('/') || targetUrl?.includes(':\\');

    // If it's a local path with no external markers, likely their own creation
    if (isLocalPath && !isExternal) {
      return 'creating_own';
    }

    // If external source, they're evaluating something
    if (isExternal) {
      return 'evaluating_before_use';
    }

    // Default to evaluating external
    return 'evaluating_before_use';
  };

  const scenario = detectScenario();

  // ============================================
  // TOOL CATEGORY DETECTION
  // ============================================
  const detectToolCategory = (): ToolCategory => {
    const type = scanType?.toLowerCase() || '';
    const name = pluginName?.toLowerCase() || '';
    const desc = (toolDescription || toolInfo?.description || '').toLowerCase();

    if (type === 'mcp' || name.includes('mcp') || desc.includes('model context protocol')) {
      return 'mcp';
    }
    if (
      type === 'agent' ||
      name.includes('agent') ||
      desc.includes('autonomous') ||
      desc.includes('ai agent')
    ) {
      return 'agent';
    }
    if (
      type === 'workflow' ||
      name.includes('workflow') ||
      desc.includes('automation') ||
      desc.includes('pipeline')
    ) {
      return 'workflow';
    }
    if (desc.includes('api') || desc.includes('endpoint') || desc.includes('rest')) {
      return 'api';
    }
    if (desc.includes('server') || name.includes('server')) {
      return 'server';
    }
    if (
      type === 'plugin' ||
      name.includes('plugin') ||
      desc.includes('plugin') ||
      desc.includes('extension')
    ) {
      return 'plugin';
    }
    return 'code';
  };

  const toolCategory = detectToolCategory();

  // ============================================
  // FRIENDLY NAMES & ICONS
  // ============================================
  const getToolTypeFriendlyName = (): string => {
    switch (toolCategory) {
      case 'mcp':
        return 'plugin for your AI assistant';
      case 'agent':
        return 'AI helper that can work on its own';
      case 'workflow':
        return 'automated process';
      case 'api':
        return 'online service connection';
      case 'server':
        return 'background service';
      case 'plugin':
        return 'add-on tool';
      default:
        return 'software tool';
    }
  };

  const toolTypeName = getToolTypeFriendlyName();
  const ToolIcon = TOOL_ICON_MAP[toolCategory] ?? Code;

  // ============================================
  // SCENARIO-SPECIFIC CONTENT
  // ============================================

  const getTitle = (): string => {
    switch (scenario) {
      case 'evaluating_before_use':
        return 'Before You Use This: A Friendly Safety Check';
      case 'evaluating_after_use':
        return "About That Tool You've Been Using";
      case 'evaluating_installed':
        return 'A Checkup on Something in Your System';
      case 'creating_own':
        return 'A Friendly Note About Your Creation';
      case 'using_others_code':
        return "About the Code You're Borrowing";
      case 'hybrid':
        return "A Look at Your Project's Building Blocks";
      default:
        return 'A Friendly Safety Report';
    }
  };

  const getGreeting = (): string => {
    const creatorMention =
      creatorName && creatorName !== 'Unknown' ? ` (made by ${creatorName})` : '';

    switch (scenario) {
      case 'evaluating_before_use':
        return `You're thinking about using "${pluginName}"${creatorMention} — a ${toolTypeName}. Smart move checking it first! It's like reading the reviews before buying something online, or having a trusted friend look under the hood of a used car before you drive it home.`;

      case 'evaluating_after_use':
        return `You've been using "${pluginName}"${creatorMention}, and now you want to make sure it's as safe as you thought. That's really wise — like getting a home inspection on a house you've been renting, just to know what you're dealing with.`;

      case 'evaluating_installed':
        return `"${pluginName}"${creatorMention} is already part of your setup. Let's take a look under the hood and make sure everything is shipshape — like having a mechanic check your car during a routine oil change.`;

      case 'creating_own':
        return `Congratulations! You've created "${pluginName}" — and that's genuinely impressive! Whether you used an AI assistant to help you build it or wrote every piece yourself, turning an idea into something real is worth celebrating. Now let's make sure your creation is as safe as it is clever.`;

      case 'using_others_code':
        return `You're planning to use code from "${pluginName}"${creatorMention} in your own project. That's common and often smart — why reinvent the wheel? But it's also wise to check what you're bringing into your house before you make it part of the foundation.`;

      case 'hybrid':
        return `Your project includes both your own work and pieces from others, including "${pluginName}"${creatorMention}. That's how most software gets built these days! Let's check that all the pieces play nicely together and don't have any hidden surprises.`;

      default:
        return `Let's take a look at "${pluginName}" and make sure it's safe to use.`;
    }
  };

  const getWhatThisIsSection = (): { title: string; content: string } => {
    const desc = toolDescription || toolInfo?.description || '';
    const capabilities = toolInfo?.capabilities?.slice(0, 3).join(', ') || '';

    switch (toolCategory) {
      case 'mcp':
        return {
          title: 'What This Plugin Does',
          content: `This is a ${toolTypeName}. Think of your AI assistant (like Claude) as a helpful friend who lives in your computer. This plugin is like giving that friend a new skill — maybe the ability to read your emails, search the web, or work with your files. ${desc ? `Specifically, this one: ${desc}` : ''} ${capabilities ? `It can do things like: ${capabilities}.` : ''} That's powerful and useful, but you want to make sure you're comfortable with what abilities you're giving it.`,
        };

      case 'agent':
        return {
          title: 'What This AI Helper Does',
          content: `This is an ${toolTypeName}. Unlike a regular program that only does exactly what you tell it, this can make decisions and take actions on its own — like a very eager assistant who doesn't wait for instructions on every little thing. ${desc ? `This one is designed to: ${desc}` : ''} That independence is what makes it useful, but it's also why we want to make sure it has good judgment and proper boundaries.`,
        };

      case 'workflow':
        return {
          title: 'What This Automation Does',
          content: `This is an ${toolTypeName} — a series of steps that run automatically, like a Rube Goldberg machine for digital tasks. Once you set it up, it works without you having to push any buttons. ${desc ? `This one: ${desc}` : ''} Automation is wonderful for saving time, but you want to make sure the machine won't accidentally knock over anything important while it's running.`,
        };

      case 'api':
        return {
          title: 'What This Connection Does',
          content: `This connects your software to an ${toolTypeName}. Think of it like a phone line to another business — your software can call them up and ask for information or services. ${desc ? `This service: ${desc}` : ''} Just like you'd want to know who's on the other end of a business call, we want to make sure this connection is trustworthy.`,
        };

      case 'server':
        return {
          title: 'What This Service Does',
          content: `This is a ${toolTypeName} that runs quietly in the background, like a helpful butler who's always on duty. ${desc ? `Its job is to: ${desc}` : ''} Background services are powerful because they're always working, which is also why we want to make sure they're behaving properly when no one's watching.`,
        };

      default:
        return {
          title: 'What This Software Does',
          content: `This is a ${toolTypeName}. ${desc ? desc : 'It performs various computing tasks.'} ${capabilities ? `Some of its abilities include: ${capabilities}.` : ''} Let's make sure it does what it promises without any unexpected side effects.`,
        };
    }
  };

  const getSecurityAnalogy = (): string => {
    const actionPhrase =
      scenario === 'creating_own'
        ? 'your creation has'
        : scenario === 'evaluating_after_use' || scenario === 'evaluating_installed'
          ? "the tool you're using has"
          : 'this tool has';

    if (hasCritical) {
      if (hasSecrets) {
        return `We found ${vulnerabilities.critical} serious problems — ${actionPhrase} the digital equivalent of house keys taped to the front door. Passwords or secret codes are visible to anyone who knows where to look. This needs to be fixed before anything else.`;
      }
      if (hasInjection) {
        return `We found ${vulnerabilities.critical} serious problems — there are gaps that could let troublemakers slip commands in sideways, like someone passing notes through a mail slot that end up controlling your whole house. This is a significant safety issue.`;
      }
      return `We found ${vulnerabilities.critical} serious problems — think of them like windows that don't lock properly. Everything might look fine from the outside, but the protection isn't there when you need it.`;
    }

    if (hasHigh) {
      return `We found ${vulnerabilities.high} important concerns — like a fence with some loose boards. The main structure is there, but a determined troublemaker could find a way through. Worth fixing, but not an emergency.`;
    }

    if (totalIssues > 5) {
      return `We found ${totalIssues} smaller issues — like a house that needs some caulking around the windows and a fresh coat of paint. Nothing urgent, but a little attention would make everything more solid.`;
    }

    if (totalIssues > 0) {
      return `We found just ${totalIssues} minor things — like a squeaky door hinge or a drafty window. Normal wear and tear, nothing to worry about.`;
    }

    return `Good news! Everything looks solid — like a well-built house with good locks, sturdy walls, and no surprises hiding in the basement.`;
  };

  const getCreatorSection = (): string => {
    if (scenario === 'creating_own') {
      // For their own creations, talk about the components/dependencies they used
      if (metadata?.repoMetadata?.language) {
        return `Your project uses ${metadata.repoMetadata.language} and various building blocks from the wider software community. Most of these are well-established and trustworthy — like buying supplies from reputable hardware stores.`;
      }
      return `You've assembled this from various pieces, some from your own imagination and some from the wider world of software. That's completely normal and usually safe.`;
    }

    // For external tools
    if (!creatorVerified || creatorName === 'Unknown') {
      return `About who made this: we couldn't fully verify the creator${creatorName && creatorName !== 'Unknown' ? ` (${creatorName})` : ''}. It's like buying from a new seller online who doesn't have many reviews yet — they might be perfectly fine, but there's no track record to check. That doesn't mean it's bad, just that you're taking their work on faith.`;
    }

    const stars = metadata?.repoMetadata?.stars;
    const starsPhrase =
      stars && stars > 1000
        ? ` with ${stars.toLocaleString()} people following their work`
        : stars && stars > 100
          ? ` with a good-sized following`
          : '';

    return `The creator (${creatorName}) has a verifiable track record${starsPhrase}. It's like buying from a shop that's been in business for years with plenty of happy customers. That's reassuring, though of course we still want to check the specific item you're getting.`;
  };

  const getVerdict = () => {
    const isDO_NOT = recommendation === 'DO_NOT_INSTALL' || hasCritical;
    const isCAUTION = hasHigh || recommendation === 'CAUTION' || recommendation === 'CONDITIONAL';

    if (isDO_NOT) {
      return {
        icon: XCircle,
        color: 'text-[hsl(var(--risk-critical))]',
        bg: 'bg-[hsl(var(--risk-critical-bg))]',
        border: 'border-[hsl(var(--risk-critical)/0.3)]',
        title:
          scenario === 'creating_own'
            ? 'Hold Off On Sharing This'
            : scenario === 'evaluating_after_use' || scenario === 'evaluating_installed'
              ? 'Consider Removing This For Now'
              : "We'd Recommend Against Using This",
        message:
          scenario === 'creating_own'
            ? `Your creation has real potential, but it's not ready to share with others yet. It's like a beautiful cake that looks great but has raw eggs in the middle — you need to finish baking it properly. Get help from someone tech-savvy to address these ${vulnerabilities.critical} serious issues, and then you'll have something you can be proud to share.`
            : scenario === 'evaluating_after_use' || scenario === 'evaluating_installed'
              ? `We know this might be inconvenient since you're already using it, but the ${vulnerabilities.critical} serious problems we found are significant enough that we'd recommend finding an alternative or pausing use until the creator fixes these issues. Your safety is more important than convenience.`
              : `This ${toolTypeName} has ${vulnerabilities.critical} serious safety problems. It's like a beautiful used car with a great price — but the brakes don't work. No matter how nice everything else looks, you wouldn't drive it home. Look for an alternative, or wait for the creator to fix these issues.`,
      };
    }

    if (isCAUTION) {
      return {
        icon: AlertTriangle,
        color: 'text-[hsl(var(--risk-medium))]',
        bg: 'bg-[hsl(var(--risk-medium-bg))]',
        border: 'border-[hsl(var(--risk-medium)/0.3)]',
        title: scenario === 'creating_own' ? 'Almost There — Needs Polish' : 'Okay for Careful Use',
        message:
          scenario === 'creating_own'
            ? `You're on the right track! Your creation works, but it could use some refinement before you share it widely or use it for anything important. It's like a bicycle that rides fine but needs the brakes tightened — okay for around the block, not ready for a mountain trail. When you're ready, have someone knowledgeable look it over.`
            : `You can use this ${toolTypeName} for simple, low-stakes tasks — like trying a new recipe before serving it at a dinner party. But we wouldn't recommend connecting it to anything sensitive or giving it access to important information until the ${vulnerabilities.high} concerns we found are addressed. Keep an eye out for updates.`,
      };
    }

    return {
      icon: CheckCircle,
      color: 'text-[hsl(var(--risk-low))]',
      bg: 'bg-[hsl(var(--risk-low-bg))]',
      border: 'border-[hsl(var(--risk-low)/0.3)]',
      title: scenario === 'creating_own' ? 'Nice Work — Looking Good!' : 'Looks Safe to Use',
      message:
        scenario === 'creating_own'
          ? `Congratulations! Your creation passed our safety check with flying colors. It's solid, it's safe, and you should feel good about what you've built. Of course, if you ever expand it significantly, another check wouldn't hurt — but for now, enjoy your accomplishment!`
          : `This ${toolTypeName} passed our inspection well. It's like a reliable appliance from a reputable brand — you can use it with confidence. Start with non-sensitive tasks to get comfortable, then use it however you need. Nice choice!`,
    };
  };

  const getClosing = (): string => {
    switch (scenario) {
      case 'creating_own':
        return "Remember: every expert was once a beginner, and asking for a second opinion is wisdom, not weakness. You're doing great by getting this checkup. Keep creating, keep learning, and keep being curious.";

      case 'evaluating_before_use':
        return "Remember: there are lots of tools out there. If this one isn't quite right, that's okay — the perfect one for your needs exists somewhere. Checking reports like this before installing things? That's exactly what smart, careful people do.";

      case 'evaluating_after_use':
      case 'evaluating_installed':
        return "It takes courage to check something you're already using — it's much easier to assume everything is fine. By looking into this, you're taking good care of yourself and your digital life.";

      default:
        return 'Stay curious, stay safe, and keep exploring. The digital world has amazing things to offer, and a little caution helps you enjoy them with peace of mind.';
    }
  };

  const verdict = getVerdict();
  const VerdictIcon = verdict.icon;
  const whatThisIs = getWhatThisIsSection();

  return (
    <div className="bg-card border border-border rounded-lg p-8 print:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8 pb-6 border-b-2 border-primary/20">
        <div className="flex justify-center mb-4">
          <div className="bg-gradient-to-r from-primary/20 to-secondary/20 p-4 rounded-full">
            <ToolIcon className="w-10 h-10 text-primary" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">{getTitle()}</h1>
        <p className="text-muted-foreground">
          {pluginName}{' '}
          {creatorName && creatorName !== 'Unknown' && scenario !== 'creating_own'
            ? `by ${creatorName}`
            : ''}
          <span className="mx-2">•</span>
          {new Date(reportDate).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </div>

      {/* Letter Content */}
      <div className="space-y-6 text-base leading-relaxed text-foreground">
        {/* Greeting */}
        <p className="text-lg">
          <span className="font-semibold">Dear Friend,</span>
        </p>

        <p>{getGreeting()}</p>

        {/* What This Tool Does */}
        <div className="bg-primary/5 rounded-lg p-4 border-l-4 border-primary">
          <p className="font-medium text-primary mb-2 flex items-center gap-2">
            <ToolIcon className="w-4 h-4" />
            {whatThisIs.title}
          </p>
          <p className="text-muted-foreground">{whatThisIs.content}</p>
        </div>

        <p>
          We ran a thorough safety check — think of it like a home inspection, but for software.
          Here's what we found:
        </p>

        {/* The Inspection Results */}
        <div className="bg-muted/30 rounded-lg p-4 space-y-4">
          <div>
            <p className="font-medium mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              Safety Check Results
            </p>
            <p>{getSecurityAnalogy()}</p>
          </div>

          <div>
            <p className="font-medium mb-2 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              About the Source
            </p>
            <p>{getCreatorSection()}</p>
          </div>
        </div>

        {/* The Verdict */}
        <div className={`rounded-lg p-5 ${verdict.bg} border ${verdict.border}`}>
          <div className="flex items-start gap-3">
            <VerdictIcon className={`w-6 h-6 ${verdict.color} flex-shrink-0 mt-0.5`} />
            <div>
              <p className={`font-bold text-lg ${verdict.color} mb-2`}>{verdict.title}</p>
              <p className="text-foreground">{verdict.message}</p>
            </div>
          </div>
        </div>

        {/* Closing */}
        <p>{getClosing()}</p>

        {/* Signature */}
        <div className="pt-4 mt-6 border-t border-border">
          <p className="text-muted-foreground italic">With care,</p>
          <p className="font-semibold text-primary mt-1">Your AI Traffic Light Security Team</p>
          <p className="text-sm text-muted-foreground mt-2">
            P.S. — The detailed technical report follows. Share it with anyone who can help you
            understand the specifics or make fixes.
          </p>
        </div>
      </div>

      {/* Page Break for Print */}
      <div className="print:break-after-page" />
    </div>
  );
};

export default GrandmaLetter;
